import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { getItemsCountLabel } from 'common/utils';
import i18n from 'i18n';
import { addAppConfirmationDialog, addAppSuccessMessage } from 'modules/alerts/state';
import { isApiResponse } from 'modules/api/utils';
import { AppRoutes } from 'modules/router';
import { push } from 'redux-first-history';
import { handleQueryError } from 'store/utils';

import { communityActions } from './actions';
import { communityApi } from './communityApi';
import { registerToCommunitySuccessEffect } from './effects';

import type { Effect, RootState } from 'store';

export const communityApiListener = createListenerMiddleware<RootState>();

const handleApiErrors =
  (defaultMessage = 'Unexpected error occurred'): Effect =>
  ({ type, payload }, { dispatch }) => {
    const response = isApiResponse(payload) ? payload : undefined;
    handleQueryError(payload, dispatch, type, response?.data || defaultMessage);
  };

communityApiListener.startListening({
  matcher: communityApi.endpoints.createCommunity.matchRejected,
  effect: handleApiErrors('Unable to create Community'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.removeCommunityMember.matchRejected,
  effect: handleApiErrors('Unable to remove Community Member'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.updateCurrentCommunityMember.matchRejected,
  effect: handleApiErrors('Unable to update user data'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.updateMySocials.matchRejected,
  effect: handleApiErrors('Unable to update social media'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.toggleCommunityAdmin.matchRejected,
  effect: handleApiErrors('Unable to update Community Member role'),
});

communityApiListener.startListening({
  actionCreator: communityActions.community.registeredToCommunity,
  effect: registerToCommunitySuccessEffect,
});

// TODO: remove below code and uncomment line 308 from communityApi.ts when BE fixed
communityApiListener.startListening({
  matcher: communityApi.endpoints.submitCronofyCode.matchFulfilled,
  effect: (_args, { dispatch, getState }) => {
    const {
      community: { activeCommunityId },
    } = getState();

    if (activeCommunityId) {
      dispatch(
        communityApi.util.updateQueryData('getCurrentCommunityMember', { communityId: activeCommunityId }, (data) => {
          data.hasCronofyCredentials = true;
        })
      );
    }
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.updateMyCommunity.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(addAppSuccessMessage('Your information has been saved'));
  },
});

communityApiListener.startListening({
  matcher: isAnyOf(
    communityApi.endpoints.updateCurrentCommunityMember.matchFulfilled,
    communityApi.endpoints.updateMySocials.matchFulfilled
  ),
  effect: (_, { dispatch }) => {
    dispatch(addAppSuccessMessage('Your information has been saved'));
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.updateMySocials.matchRejected,
  effect: handleApiErrors('Unexpected error when updating socials'),
});

communityApiListener.startListening({
  matcher: isAnyOf(
    communityApi.endpoints.updateMyCommunity.matchRejected,
    communityApi.endpoints.uploadLogo.matchRejected,
    communityApi.endpoints.updateCommunityBranding.matchRejected
  ),
  effect: handleApiErrors('Unable to update Community'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.inviteGuestsToCommunity.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(addAppSuccessMessage('Invitation has been sent'));
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.inviteGuestsToCommunity.matchRejected,
  effect: handleApiErrors('Unable to invite to the Community'),
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.toggleCommunityAdmin.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { isPromote, displayName } = meta.arg.originalArgs;
    if (isPromote) {
      dispatch(
        addAppConfirmationDialog({
          title: i18n.t('community:promoteToAdmin.successMessage', { displayName: displayName || 'User' }),
          description: '',
          confirmLabel: i18n.t('community:promoteToAdmin.confirmLabel'),
        })
      );
    } else {
      dispatch(
        addAppConfirmationDialog({
          title: i18n.t('community:demoteFromAdmin.successMessage', { displayName: displayName || 'User' }),
          description: '',
          confirmLabel: i18n.t('community:demoteFromAdmin.confirmLabel'),
        })
      );
    }
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.removeCommunityMember.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { displayName } = meta.arg.originalArgs;
    dispatch(
      addAppConfirmationDialog({
        title: `${displayName || 'User'} has been successfully removed from this Community`,
        description: '',
        confirmLabel: 'Got it',
      })
    );
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.updateCommunityBranding.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(push(AppRoutes.Settings));
  },
});

communityApiListener.startListening({
  matcher: communityApi.endpoints.inviteGuestsToCommunity.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    const { guests } = meta.arg.originalArgs;
    dispatch(
      addAppConfirmationDialog({
        title: 'Invitation has been sent',
        description: `You have invited ${getItemsCountLabel('new Member', guests.length)} to your Community.`,
        confirmLabel: 'Understood',
        maxWidth: 'sm',
      })
    );
  },
});
